import React from 'react';
import { useTranslation } from 'react-i18next';

function Footer() {
    const { t } = useTranslation();

    const navLinks = [
        { name: t('navbar.home'), href: "#home" },
        { name: t('navbar.about'), href: "#about" },
        { name: t('navbar.experience'), href: "#experience" },
        { name: t('navbar.projects'), href: "#projects" },
        { name: t('navbar.contact'), href: "#contact" },
    ];

    return (
        <footer className="relative py-12 px-4 md:px-8 bg-base-300 overflow-hidden">
            {/* Blurred decorative shapes */}
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-accent/20 dark:bg-accent/40 rounded-full filter blur-3xl opacity-70 dark:opacity-30 animate-blob"></div>
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-secondary/20 dark:bg-secondary/40 rounded-full filter blur-3xl opacity-70 dark:opacity-30 animate-blob animation-delay-2000"></div>
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-primary/20 dark:bg-primary/40 rounded-full filter blur-3xl opacity-70 dark:opacity-30 animate-blob animation-delay-4000"></div>

            {/* Gradient top border */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent"></div>

            <div className="container mx-auto max-w-7xl">
                {/* Main footer content */}
                <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                    {/* Logo and navigation */}
                    <div className="flex flex-col md:flex-row items-center gap-8 w-full lg:w-auto">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 405 492"
                            className='fill-current w-20 h-20 md:w-24 md:h-24'
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <g transform="translate(0,492) scale(0.1,-0.1)" stroke="none">
                                <path d="M2009 3538 l-65 -114 313 -535 c173 -293 373 -633 446 -754 l132 -220 129 -3 c122 -2 128 -2 124 16 -6 23 -947 1627 -999 1702 -15 22 -16 21 -80 -92z" />
                                <path d="M1493 3363 c-72 -120 -235 -396 -363 -613 -128 -217 -298 -504 -377 -637 -79 -133 -146 -249 -149 -257 -5 -14 10 -16 127 -16 l134 1 292 497 c160 273 329 561 375 640 80 138 83 142 98 121 8 -13 146 -246 306 -518 l291 -496 -239 -5 -240 -5 -64 -110 c-35 -60 -64 -113 -64 -117 0 -5 170 -8 379 -8 l378 0 124 -215 124 -214 265 -1 c146 -1 320 -3 388 -6 67 -3 122 -2 122 2 0 4 -30 58 -66 120 l-67 114 -251 0 -252 0 -18 33 c-33 57 -148 253 -368 627 -118 201 -331 563 -473 805 -142 242 -263 448 -270 458 -11 16 -28 -8 -142 -200z" />
                                <path d="M1501 2522 c-62 -108 -203 -350 -314 -538 l-200 -341 -119 -6 c-66 -3 -179 -3 -251 0 -154 7 -133 19 -219 -126 -56 -93 -57 -96 -35 -103 12 -3 185 -6 384 -7 l362 -1 19 28 c22 32 317 529 424 717 l75 130 117 3 c104 2 117 5 112 19 -9 23 -212 385 -228 405 -11 15 -27 -7 -127 -180z" />
                            </g>
                        </svg>

                        {/* Navigation links */}
                        <nav className="flex flex-wrap justify-center gap-4 md:gap-6">
                            {navLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.href}
                                    className="link link-hover font-medium text-base hover:text-primary transition-colors duration-200"
                                >
                                    {link.name}
                                </a>
                            ))}
                        </nav>
                    </div>

                    {/* Social links */}
                    <div className="flex items-center gap-4 md:gap-6">
                        <a href="https://github.com/AliAmeur20" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-circle hover:bg-base-200">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                className="fill-current"
                            >
                                <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"></path>
                            </svg>
                        </a>
                        <a href="https://www.youtube.com/@triangles-dev" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-circle hover:bg-base-200">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                className="fill-current"
                            >
                                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"></path>
                            </svg>
                        </a>
                        <a href="https://www.linkedin.com/in/ali-ameur-56b761299/" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-circle hover:bg-base-200 text-2xl font-extrabold">
                            in
                        </a>
                    </div>
                </div>

                {/* Copyright text - now at the bottom */}
                <div className="mt-8 pt-6 border-t border-base-content/10 text-center md:text-left">
                    <p className="text-sm opacity-80">Copyright © {new Date().getFullYear()} - {t('footer.rights')}</p>
                </div>
            </div>
        </footer>
    );
}

export default Footer;